import { useState } from "react";
import { authFetch } from "../api";

const SERVICES = [
  { key: "water",           label: "Water",          icon: "💧" },
  { key: "ors",             label: "ORS",            icon: "🧃" },
  { key: "mobile_charging", label: "Charging",       icon: "🔌" },
  { key: "rest_area",       label: "Rest Area",      icon: "🛏️" },
  { key: "training",        label: "Training",       icon: "📋" },
  { key: "first_aid",       label: "First Aid",      icon: "🩺" },
  { key: "health_camp",     label: "Health Camp",    icon: "🏥" },
];

export default function ExitLog({ center, apiBase }) {
  const [selected, setSelected]       = useState([]);
  const [restMinutes, setRestMinutes] = useState("");
  const [saving, setSaving]           = useState(false);
  const [flash, setFlash]             = useState(null);
  const [todayCount, setTodayCount]   = useState(null);

  const localToday = new Date().toLocaleDateString("en-CA");

  // Load today's exit count on mount
  useState(() => {
    authFetch(`${apiBase}/centers/${center.id}/exit/summary?date=${localToday}`)
      .then(r => r.json())
      .then(d => setTodayCount(d.total ?? 0))
      .catch(() => {});
  });

  const toggle = (key) =>
    setSelected(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );

  const handleLog = async () => {
    setSaving(true);
    try {
      const body = {
        services: selected,
        local_date: localToday,
        ...(selected.includes("rest_area") && restMinutes
          ? { rest_area_minutes: parseInt(restMinutes, 10) }
          : {}),
      };
      const res = await authFetch(`${apiBase}/centers/${center.id}/exit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error();
      setFlash({ type: "ok", text: "Exit logged!" });
      setTodayCount(c => (c ?? 0) + 1);
      setSelected([]);
      setRestMinutes("");
      setTimeout(() => setFlash(null), 2000);
    } catch {
      setFlash({ type: "err", text: "Error — try again" });
      setTimeout(() => setFlash(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.card}>

      {/* Header */}
      <div style={styles.header}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>Log Exit</div>
        <div style={{ fontSize: 13, opacity: 0.8, marginTop: 2 }}>{center.name}</div>
      </div>

      {/* Today counter */}
      {todayCount !== null && (
        <div style={styles.counter}>
          <span style={{ fontSize: 32, fontWeight: 700, color: "#d94f2b" }}>{todayCount}</span>
          <span style={{ fontSize: 13, color: "#555", marginLeft: 8 }}>exits today</span>
        </div>
      )}

      {/* Flash */}
      {flash && (
        <div style={{
          ...styles.flash,
          background: flash.type === "ok" ? "#e6f9ed" : "#fdecea",
          color:      flash.type === "ok" ? "#1a5c31" : "#8B2020",
        }}>
          {flash.text}
        </div>
      )}

      {/* Services */}
      <div style={styles.sectionLabel}>Services consumed (select all that apply)</div>
      <div style={styles.serviceGrid}>
        {SERVICES.map(s => {
          const active = selected.includes(s.key);
          return (
            <button
              key={s.key}
              onClick={() => toggle(s.key)}
              style={serviceBtn(active)}
            >
              <span style={{ fontSize: 24 }}>{s.icon}</span>
              <span style={{ fontSize: 12, fontWeight: active ? 700 : 400, marginTop: 4 }}>{s.label}</span>
            </button>
          );
        })}
      </div>

      {/* Rest area minutes — only shown when rest area is selected */}
      {selected.includes("rest_area") && (
        <div style={styles.restRow}>
          <label style={{ fontSize: 14, color: "#444" }}>Time spent in rest area</label>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
            <input
              type="number"
              min="0"
              placeholder="—"
              value={restMinutes}
              onChange={e => setRestMinutes(e.target.value)}
              style={styles.restInput}
            />
            <span style={{ fontSize: 13, color: "#888" }}>minutes (optional)</span>
          </div>
        </div>
      )}

      {/* Log button */}
      <button
        onClick={handleLog}
        disabled={saving}
        style={styles.logBtn(saving)}
      >
        {saving ? "Saving…" : "Log Exit"}
      </button>

    </div>
  );
}

const styles = {
  card: {
    background: "#fff", borderRadius: 16,
    padding: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  header: {
    background: "#d94f2b", color: "#fff",
    borderRadius: 10, padding: "14px 18px",
    marginBottom: 20,
  },
  counter: {
    display: "flex", alignItems: "baseline",
    background: "#fff5f3", borderRadius: 10,
    padding: "12px 16px", marginBottom: 20,
    border: "1px solid #ffd5cc",
  },
  flash: {
    borderRadius: 8, padding: "10px 14px",
    marginBottom: 16, fontSize: 14, fontWeight: 500,
    textAlign: "center",
  },
  sectionLabel: {
    fontSize: 12, fontWeight: 700, color: "#888",
    textTransform: "uppercase", letterSpacing: "0.05em",
    marginBottom: 12,
  },
  serviceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 8,
    marginBottom: 20,
  },
  restRow: {
    background: "#fafafa", borderRadius: 10,
    padding: "14px 16px", marginBottom: 20,
    border: "1px solid #eee",
  },
  restInput: {
    width: 72, padding: "8px 10px",
    border: "1px solid #ddd", borderRadius: 8,
    fontSize: 16, textAlign: "center", outline: "none",
  },
  logBtn: (disabled) => ({
    width: "100%", padding: "16px 0",
    background: disabled ? "#d0d0d0" : "#d94f2b",
    color: "#fff", border: "none", borderRadius: 10,
    fontSize: 17, fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "background 0.15s",
  }),
};

function serviceBtn(active) {
  return {
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    padding: "10px 4px", borderRadius: 10,
    border: active ? "2px solid #d94f2b" : "2px solid #e8e8e8",
    background: active ? "#fff0ed" : "#fafafa",
    cursor: "pointer", transition: "all 0.1s",
    minHeight: 68,
  };
}
